<template>
	<screen class="accounts-view">
		<template slot="header">
			<ui-header type="dark" @click="onBack">
				<h2 slot="title">Linked Accounts</h2>
				Select the cards you want the app to round up for donations.
			</ui-header>
		</template>
		<div class="link-list">
			<ul v-for="link in links" :key="link.id">
				<div
					class="link-header"
					:class="[!link.is_valid && 'not-valid']"
				>
					<span>{{ link.plaid_institution_name }}</span>
					<div
						class="link-update"
						:class="[!link.is_valid && 'not-valid']"
						@click="openLink(link)"
					>
						i
					</div>
				</div>
				<div class="accounts-list">
					<li
						class="account-item"
						v-for="account in link.accounts"
						:key="account.id"
					>
						<div class="account-text">
							<span class="account-name">{{ account.name }}</span>
							<span class="account-mask"
								>**** {{ account.mask }}</span
							>
						</div>
						<div class="account-toggle">
							<ui-toggle
								:checked="account.deleted_at"
								@input="checked(account)"
							></ui-toggle>
						</div>
					</li>
					<div v-if="links.length <= 0">
						<h3>No Accounts</h3>
						<p>
							It looks like this bank has no accounts that we can
							use!
						</p>
					</div>
				</div>
			</ul>
			<div v-if="links.length <= 0">
				<h3>No Cards</h3>
				<p>
					Before you can keep track of round ups we will need you to
					link one of your banks!
				</p>
			</div>
			<div class="accounts-actions" v-if="linkToken">
				<plaid-link
					v-bind="{
						env,
						webhook,
						clientName,
						onSuccess,
						token: linkToken
					}"
				>
					+ Add Another Card
				</plaid-link>
			</div>
			<div class="accounts-actions" v-else>
				<ui-button loading="true" disabled="true">
					+ Add Another Card
				</ui-button>
			</div>
		</div>
		<modal v-model="showModal" v-if="link" size="medium">
			<div class="link">
				<h3 style="margin-bottom: 0px" class="link-title">
					{{ link.plaid_institution_name }}
				</h3>
				<p
					style="
						color: red;
						font-size: 14px;
						margin-bottom: 10px;
						margin-top: 0px;
					"
					v-if="!link.is_valid"
				>
					We cannot reach your bank. Please re-authorize us to compute
					RoundUps
				</p>
				<ui-button class="link-option" @click="updateLink"
					>Re-Authorize RoundUps</ui-button
				>
				<div class="link-option" @click="removeLink">Delete Bank</div>
			</div>
		</modal>
	</screen>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import routable from '../../mixins/routable'
import UiButton from '../../ui/Button'
import UiToggle from '../../ui/Toggle'
import UiHeader from '../../ui/Header'
import Modal from '../../ui/Modal'
import Screen from '../../ui/Screen'
import PlaidLink from 'vue-plaid-link'

export default {
	name: 'accounts-view',
	// eslint-disable-next-line vue/no-unused-components
	components: { UiToggle, UiHeader, Screen, Modal, PlaidLink, UiButton },
	mixins: [routable],
	props: {
		nextButton: {
			type: String,
			default: 'Next'
		}
	},
	data() {
		return {
			publicKey: process.env.VUE_APP_PLAID_PUBLIC_KEY,
			env: process.env.VUE_APP_PLAID_ENV,
			webhook: process.env.VUE_APP_PLAID_WEBHOOK,
			clientName: 'RoundUp App',
			link: null,
			showModal: false,
			linkToken: null
		}
	},
	computed: {
		...mapGetters(['links'])
	},
	methods: {
		...mapActions([
			'attachLink',
			'getLinks',
			'getPublicToken',
			'deleteLink',
			'toggleAccount',
			'refreshLink',
			'fetchLinkToken'
		]),
		onSuccess(token, metadata) {
			const params = {
				public_token: token,
				institution_id: metadata.institution.institution_id,
				institution_name: metadata.institution.name
			}
			this.attachLink(params).catch((err) =>
				this.$swal('Error', err.message)
			)
			this.updateLinkToken()
		},
		onBack() {
			const path = localStorage.prevPage || '/Settings'
			this.navigate({ path }, 'back')
		},
		checked(account) {
			this.toggleAccount(account)
		},
		openLink(link) {
			this.link = link
			this.showModal = true
		},
		updateLink() {
			this.getPublicToken(this.link)
				.then((data) => {
					const handler = window.Plaid.create({
						clientName: this.clientName,
						env: this.env,
						key: this.publicKey,
						onSuccess: () => {
							this.refreshLink(this.link).then(() => {
								this.showModal = false
							})
						},
						product: 'transactions',
						token: data.public_token,
						webhook: this.webhook
					})
					handler.open()
				})
				.catch(() => {
					this.$swal(
						'Error',
						'It looks like we are unable to relink your bank at this time. Please contact support.'
					)
				})
		},
		removeLink() {
			if (
				confirm(
					'Are you sure you want to remove this bank from your account?'
				)
			) {
				this.deleteLink(this.link)
					.then(() => {
						this.showModal = false
					})
					.catch(() => {
						this.$swal(
							'Error',
							'Unable to remove this bank at this time. Please contact support.'
						)
					})
			}
		},
		updateLinkToken() {
			this.linkToken = null
			this.fetchLinkToken()
				.then((resp) => {
					this.linkToken = resp.link_token
				})
				.catch((error) => this.$swal('Error', error.message))
		}
	},
	mounted() {
		this.getLinks()
		this.updateLinkToken()
	}
}
</script>

<style lang="scss">
.accounts-view {
	background: $bone;
	overflow: hidden;
	position: relative;
	.link-list {
		background: $white;
		margin: 10px -25px 0px;
		position: relative;
		padding: 10px 20px 20px;
		z-index: 10;
		border-radius: 14px;
		box-shadow: 0px 3px 30px rgba(0, 0, 0, 0.15);
		@media screen and (max-width: 480px) {
			margin-left: -15px;
			margin-right: -15px;
		}
		ul {
			padding: 0px;
		}
		.link-header {
			display: flex;
			background: $bone;
			font-size: 14px;
			padding: 10px 0;
			margin: 10px 0;
			font-weight: 900;
			color: $dark-grey;
			text-align: center;
			border-radius: 25px;
			&.not-valid {
				border: 2px $red solid;
			}
			span {
				font-size: 14px;
				display: block;
				padding: 5px 0px;
				font-weight: 900;
				flex-grow: 1;
			}
			.link-update {
				display: flex;
				background: $dark-grey;
				color: $white;
				font-weight: 400;
				width: 30px;
				height: 30px;
				margin-top: -1px;
				margin-right: 10px;
				margin-bottom: -1px;
				cursor: pointer;
				border-radius: 20px;
				justify-content: center;
				align-items: center;
				&.not-valid {
					background: $red;
				}
			}
		}
		.accounts-list {
			.account-item {
				border-bottom: 1px solid $bone;
				padding: 10px 25px;
				margin: 0px -20px;
				text-align: left;
				list-style: none;
				display: flex;
				align-items: center;
				justify-content: center;
				.account-text {
					flex-grow: 1;
					span {
						display: block;
						&.account-mask {
							font-size: 14px;
							color: $dark-grey;
							opacity: 0.5;
						}
					}
				}
			}
		}
	}
}

.link {
	.link-title {
		font-family: 'Bitter', serif;
	}
	.link-option {
		padding: 15px 10px;
		border-top: 1px solid $lighter-grey;
		margin-left: -10px;
		margin-right: -10px;
		cursor: pointer;
		&:hover {
			background: $lighter-grey;
			color: $blue;
		}
	}
}
.plaid-link-button {
	margin-top: 20px;
	color: $white !important;
	background: $navy !important;
}
</style>
